import { Box, ClickAwayListener, useTheme } from '@mui/material'
import { useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useNavigate, useSearchParams } from 'react-router-dom'
import { PERMISSIONS } from '../../_constants'
import { useAppDispatch, useAppSelector } from '../../_helpers/hooks'
import { IRecurringBooking } from '../../_models/newBookingInterface'
import { CalenderWithTickIcon } from '../../components/icons'
import { useUserPermissionAuthorization } from '../../hooks'
import {
  changeFilters,
  dashboardState,
  getBookingList,
  getDashboardStatistics,
} from '../../slices/dashboardSlice'
import { closeBookingSuccessModal, newBookingData } from '../../slices/newBookingSlice'
import { getPricingLimits } from '../../slices/pricingSlice'
import { publicPagesState } from '../../slices/publicPagesSlice'
import ActionAlertModal from '../actionAlertModal'
import BookingDetailsModal from './bookingDetailsModal'
import RecurringSuggestionModal from './recurringSuggestionModal'

interface IBookingSuccessModal {
  showModal: boolean
  userType: string
  updateDrawer?: () => void
  isGuestReschedule?: boolean
}

const BookingSuccessModal = (props: IBookingSuccessModal) => {
  const { userType, isGuestReschedule = false } = props
  const dispatch = useAppDispatch()
  const [urlParams] = useSearchParams()
  const navigate = useNavigate()
  const { t } = useTranslation()
  const newBookingState = useAppSelector(newBookingData)
  const { isSubscriptionBased } = useAppSelector(publicPagesState)
  const { bookingSuccessInformation, bookingSuccessHeaderMessage } = newBookingState
  const { dashboardBookingListData } = useAppSelector(dashboardState)
  const { filters } = dashboardBookingListData
  const [AlertModal, setAlertModal] = useState<boolean>(false)
  const [isPermitted] = useUserPermissionAuthorization()
  const theme = useTheme()
  const locale = urlParams.get('locale')
  // Calculate booking status based on recurring booking data
  const getBookingStatus = () => {
    if (!bookingSuccessInformation?.is_recurring_booking) {
      return bookingSuccessHeaderMessage
    }

    const successfulBookings = bookingSuccessInformation.recurring_booking_data?.filter(
      (booking: IRecurringBooking) => booking.status === 'success',
    )
    const failedBookings = bookingSuccessInformation.recurring_booking_data?.filter(
      (booking: IRecurringBooking) => booking.status === 'failed',
    )

    if (failedBookings?.length === 0) {
      return t('newBooking.bookingSuccessStatus')
    } else if (successfulBookings?.length === 0) {
      return t('newBooking.bookingFailedStatus')
    } else {
      return t('newBooking.bookingPartialStatus')
    }
  }

  useEffect(() => {
    setAlertModal(props.showModal)
  }, [props.showModal])

  const closeAlertModal = (): void => {
    setAlertModal(false)
    dispatch(closeBookingSuccessModal())
    if (userType !== 'customer') {
      dispatch(changeFilters({ ...filters, search: '', page: 1 }))
      dispatch(getBookingList(filters))
      isPermitted([PERMISSIONS.DASHBOARD_KPI_VIEW]) && dispatch(getDashboardStatistics())
      if (isSubscriptionBased) {
        dispatch(getPricingLimits({}))
      }
    }
    // reset reschedule business url once reschedule is successful
    if (isGuestReschedule) {
      navigate(`?locale=${locale}`, { replace: true })
    }
  }
  const handleClickAway = (event: Event | TouchEvent) => {
    const target = event.target as HTMLElement
    // Don't close if clicking on toast elements
    if (target.closest('[role="alert"]')) {
      return
    }
    closeAlertModal() // Close the modal if clicked outside (and not on a toast)
  }
  return (
    <ActionAlertModal
      showAlert={AlertModal}
      title={getBookingStatus()}
      alertIcon={
        <CalenderWithTickIcon
          fill={theme.palette.success.light}
          stoke={theme.palette.common.white}
          width='50'
          height='51'
          backgroundColor={theme.palette.rgba.contrastText}
        />
      }
      modalContainerMaxWidth='40rem'
    >
      <ClickAwayListener onClickAway={handleClickAway}>
        <Box mt={2} sx={{ width: '100%' }}>
          {bookingSuccessInformation?.is_recurring_available ? (
            <RecurringSuggestionModal
              onClose={closeAlertModal}
              bookingSuccessInformation={bookingSuccessInformation}
            />
          ) : (
            <BookingDetailsModal
              onClose={closeAlertModal}
              bookingSuccessInformation={bookingSuccessInformation}
            />
          )}
        </Box>
      </ClickAwayListener>
    </ActionAlertModal>
  )
}

export default BookingSuccessModal
